import { Box, Typography } from "@mui/material";
import React from "react";
import AcxButton from "components/UI/AcxButton";
import theme from "Theme/AppTheme";
import AcxMainTextField, {
    AcxMainTextFieldRef,
} from "components/UI/AcxMainTextField";

type CommentComposerBaseProps = {
    inputId: string;
    title?: string;
    placeholderText: string;
    value: string;
    inputRef: React.RefObject<AcxMainTextFieldRef>;
    onFocus?: () => void;
    onChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    inputProps: React.ComponentProps<typeof AcxMainTextField>["inputProps"];
    mentionDropdown: React.ReactNode;
    actionJustify: "flex-end" | "space-between";
    onCancel: () => void;
    onSubmit: () => void;
    primaryLabel: string;
    secondaryLabel: string;
    primaryButtonId?: string;
    secondaryButtonId?: string;
    disablePrimary?: boolean;
    disableSecondary?: boolean;
    containerBackground?: string;
    inputBackground?: string;
    showTimestamp?: boolean;
    timestampValue?: string;
    onTimestampChange?: (value: string) => void;
};

const CommentComposerBase = ({
    inputId,
    title,
    placeholderText,
    value,
    inputRef,
    onFocus,
    onChange,
    inputProps,
    mentionDropdown,
    actionJustify,
    onCancel,
    onSubmit,
    primaryLabel,
    secondaryLabel,
    primaryButtonId,
    secondaryButtonId,
    disablePrimary,
    disableSecondary,
    containerBackground,
    inputBackground,
    showTimestamp,
    timestampValue,
    onTimestampChange,
}: CommentComposerBaseProps) => (
    <Box
        sx={{
            border: `1px solid ${theme.palette.gray[200]}`,
            borderRadius: 2,
            padding: 2,
            backgroundColor: containerBackground,
        }}
    >
        {title ? (
            <Typography
                sx={{
                    color: theme.palette.gray[800],
                    fontSize: "14px",
                    fontWeight: 600,
                    mb: 1,
                }}
            >
                {title}
            </Typography>
        ) : null}
        <AcxMainTextField
            id={inputId}
            ref={inputRef}
            fullWidth
            multiline
            placeholderText={placeholderText}
            rowsMin={3}
            value={value}
            onFocus={onFocus}
            onChange={onChange}
            inputProps={inputProps}
            sx={{
                "& .MuiOutlinedInput-root": {
                    backgroundColor: inputBackground,
                },
                paddingRight: 1,
            }}
        />
        {mentionDropdown}
        <Box
            sx={{
                display: "flex",
                justifyContent: actionJustify,
                alignItems: "center",
                mt: 2,
                gap: 1,
            }}
        >
            {showTimestamp && (
                <AcxMainTextField
                    id="comment-playhead-time"
                    value={timestampValue ?? ""}
                    onChange={(event) =>
                        onTimestampChange?.(event.target.value)
                    }
                    textContainerSx={{ maxWidth: "80px" }}
                    centerAlignInputText
                    disablePadding
                />
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
                <AcxButton
                    id={secondaryButtonId}
                    size="small"
                    color="white"
                    onClick={onCancel}
                    disabled={disableSecondary}
                >
                    {secondaryLabel}
                </AcxButton>
                <AcxButton
                    id={primaryButtonId}
                    size="small"
                    onClick={onSubmit}
                    disabled={disablePrimary}
                >
                    {primaryLabel}
                </AcxButton>
            </Box>
        </Box>
    </Box>
);

export default CommentComposerBase;
